import { useCallback, useEffect, useRef, useState } from 'react'
import { useEventChannel } from '@/hooks/useEventChannel'
import { apiFailure } from '@/lib/apiFailure'
import { hostsApi, hostsError, readEventHosts, readManagerInvitation } from '@/lib/hosts/api'
import { keepMadeLink, madeLinksFor } from '@/lib/hosts/madeLinks'
import type { EventHosts, HostInvitation, Manager, ManagerInvitation, ManagerInviteTarget } from '@/lib/hosts/types'

const LOAD_FAILED = 'Hosts couldn’t load.'


/** A pushed list (which carries no addresses) with the addresses the list on screen has for the same managers and
 *  invitations. */
function withAddresses(pushed: EventHosts, shown: EventHosts | null): EventHosts {
  if (!shown) return pushed
  const managers = new Map(shown.managers.map((manager) => [manager.user_id, manager.email]))
  const invitations = new Map(shown.managerInvitations.map((invitation) => [invitation.id, invitation.email]))
  return {
    ...pushed,
    managers: pushed.managers.map((manager) => (manager.email ? manager : { ...manager, email: managers.get(manager.user_id) ?? null })),
    managerInvitations: pushed.managerInvitations.map((invitation) =>
      invitation.email || invitation.binding !== 'email' ? invitation : { ...invitation, email: invitations.get(invitation.id) ?? null },
    ),
  }
}

/**
 * An event's people, for its organizers and managers: its managers and their pending invitations
 * (docs/features/event-managers.md), its hosts, its owner's stage and its pending host invitations. It loads once and
 * then follows the event's workspace channel (docs/REALTIME.md): `hosts.updated` carries the whole list as the endpoint
 * returns it whenever a manager, a stage, someone on one, or an invitation changes, so on-stage state and accepted
 * invitations show at once. After the socket drops it loads afresh; nothing polls. As in `useEventWorkspace`, a
 * completed change supersedes any list request that started before it.
 *
 * A manager invitation's link comes back only when it is made: this page keeps the ones it made
 * (`lib/hosts/madeLinks`, until the page closes or its account signs out), so they stay copyable while the list (which
 * carries no links) replaces itself, and when the view comes back.
 *
 * `hosts.updated` never carries email addresses. Those who may see them (`addresses`: the event's
 * `can_manage_managers`) take the pushed list with the addresses already on screen, then read the list again for any
 * new ones; everyone else takes the pushed list as it is.
 *
 * `loadFailed` is what to say when the list can't load (the Hosts panel's words by default).
 *
 * `selfId` is the signed-in manager's id: `onSelfRemoved` runs when a pushed list no longer has them, as when an
 * organizer removes them (docs/features/event-managers.md). `onManagerJoined` runs once for each manager a pushed list
 * brings that this page hadn't seen: someone who just accepted an invitation.
 *
 * A 404 for the list means there are no hosts to manage here (the endpoint isn't deployed, or the event is unknown):
 * `unavailable` turns true and the list stays empty.
 */
export function useEventHosts(
  eventId: string,
  {
    selfId = null,
    addresses = false,
    loadFailed = LOAD_FAILED,
    onSelfRemoved,
    onManagerJoined,
  }: {
    selfId?: string | null
    addresses?: boolean
    loadFailed?: string
    onSelfRemoved?: () => void
    onManagerJoined?: (manager: Manager) => void
  } = {},
) {
  const [data, setData] = useState<EventHosts | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [unavailable, setUnavailable] = useState(false)
  const [links, setLinks] = useState<Record<string, string>>(() => madeLinksFor(eventId))
  const listed = useRef<EventHosts | null>(null)
  listed.current = data
  const request = useRef(0)
  const mutations = useRef(0)
  const gone = useRef(false)
  // The managers this page has seen, by id; null until the list first loads, so nobody already there is announced.
  const seenManagers = useRef<Set<string> | null>(null)
  const see = (managers: Manager[]) => {
    seenManagers.current = new Set([...(seenManagers.current ?? []), ...managers.map((manager) => manager.user_id)])
  }

  const load = useCallback(
    async (quiet = false) => {
      const sequence = ++request.current
      const version = mutations.current
      if (!quiet) {
        setLoading(true)
        setError(null)
      }
      try {
        const hosts = readEventHosts(await hostsApi.list(eventId))
        if (sequence !== request.current || version !== mutations.current) return
        if (hosts) {
          see(hosts.managers)
          setData(hosts)
          setError(null)
        } else if (!quiet) {
          setError(loadFailed)
        }
      } catch (failure) {
        if (sequence !== request.current) return
        if (apiFailure(failure).status === 404) {
          gone.current = true
          setUnavailable(true)
        } else if (!quiet) {
          setError(hostsError(failure, loadFailed))
        }
      } finally {
        if (sequence === request.current) setLoading(false)
      }
    },
    [eventId, loadFailed],
  )

  useEffect(() => {
    gone.current = false
    void load()
    return () => {
      request.current++
    }
  }, [load])

  // The list as the server has it now: it supersedes any load that started before it.
  useEventChannel(eventId, {
    scope: 'workspace',
    on: {
      'hosts.updated': (data) => {
        const hosts = readEventHosts({ data })
        if (!hosts || gone.current) return
        const managed = (list: EventHosts | null) => !!list?.managers.some((manager) => manager.user_id === selfId)
        const removed = !!selfId && managed(listed.current) && !managed(hosts)
        const seen = seenManagers.current
        const joined = seen ? hosts.managers.filter((manager) => !seen.has(manager.user_id) && manager.user_id !== selfId) : []
        if (seen) see(hosts.managers)
        mutations.current++
        setData(addresses ? withAddresses(hosts, listed.current) : hosts)
        setError(null)
        // The pushed list leaves addresses out: read the list that has them.
        if (addresses) void load(true)
        joined.forEach((manager) => onManagerJoined?.(manager))
        if (removed) onSelfRemoved?.()
      },
    },
    onResync: () => {
      if (!gone.current) void load(true)
    },
  })

  const change = useCallback((update: (previous: EventHosts) => EventHosts) => {
    mutations.current++
    setData((previous) => previous && update(previous))
  }, [])

  /** Deletes, then drops the row. A 404 means it is already gone (used, revoked or removed elsewhere): the row goes
   *  and the list refreshes, so an invitation accepted meanwhile shows up as its host. */
  const deleteThen = useCallback(
    async (remove: () => Promise<void>, update: (previous: EventHosts) => EventHosts) => {
      let gone = false
      try {
        await remove()
      } catch (failure) {
        if (apiFailure(failure).status !== 404) throw failure
        gone = true
      }
      change(update)
      if (gone) void load(true)
    },
    [change, load],
  )

  const invite = useCallback(async (): Promise<HostInvitation> => {
    const { data: invitation } = await hostsApi.invite(eventId)
    change((previous) => ({
      ...previous,
      invitations: [invitation, ...previous.invitations.filter((item) => item.id !== invitation.id)],
    }))
    return invitation
  }, [eventId, change])

  const revoke = useCallback(
    (invitationId: string) =>
      deleteThen(
        () => hostsApi.revokeInvitation(eventId, invitationId),
        (previous) => ({ ...previous, invitations: previous.invitations.filter((item) => item.id !== invitationId) }),
      ),
    [eventId, deleteThen],
  )

  const removeHost = useCallback(
    (stageId: string) =>
      deleteThen(
        () => hostsApi.removeHost(eventId, stageId),
        (previous) => ({ ...previous, hosts: previous.hosts.filter((host) => host.stage_id !== stageId) }),
      ),
    [eventId, deleteThen],
  )

  /** Keeps a new manager invitation's link for this page, and lists it first. Inviting an address or an account again
   *  replaces its open invitation; each link stands alone. */
  const keepManagerInvitation = useCallback(
    (invitation: ManagerInvitation) => {
      const { url } = invitation
      if (url) {
        setLinks(keepMadeLink(eventId, invitation.id, url))
      }
      const address = invitation.email?.toLowerCase()
      const account = invitation.invitee?.user_id
      const replaced = (item: ManagerInvitation) =>
        item.id === invitation.id ||
        (!!address && item.email?.toLowerCase() === address) ||
        (!!account && item.invitee?.user_id === account)
      change((previous) => ({
        ...previous,
        managerInvitations: [invitation, ...previous.managerInvitations.filter((item) => !replaced(item))],
      }))
      return invitation
    },
    [eventId, change],
  )

  /** Invites an address, or an account by its username, to manage the event; resolves to the invitation with its link,
   *  or rejects as the API did. */
  const inviteManager = useCallback(
    async (target: ManagerInviteTarget): Promise<ManagerInvitation> => {
      const { data: body } = await hostsApi.inviteManager(eventId, target)
      // The answer as the contract has it, with the address asked for where an older API leaves that out.
      const asked = 'email' in target ? { kind: 'manager', email: target.email } : { kind: 'manager' }
      const invitation = readManagerInvitation({ ...asked, ...(body as object) })
      if (!invitation) throw new Error('The invitation answer couldn’t be read.')
      return keepManagerInvitation(invitation)
    },
    [eventId, keepManagerInvitation],
  )

  /** Makes a single-use manager link for anyone; resolves to it with its url, or rejects as the API did. */
  const createManagerLink = useCallback(async (): Promise<ManagerInvitation & { url: string }> => {
    const { data: body } = await hostsApi.createManagerLink(eventId)
    const invitation = readManagerInvitation(body)
    if (invitation?.binding !== 'link' || !invitation.url) throw new Error('The link answer couldn’t be read.')
    keepManagerInvitation(invitation)
    return { ...invitation, url: invitation.url }
  }, [eventId, keepManagerInvitation])

  const revokeManagerInvitation = useCallback(
    (invitationId: string) =>
      deleteThen(
        () => hostsApi.revokeManagerInvitation(eventId, invitationId),
        (previous) => ({
          ...previous,
          managerInvitations: previous.managerInvitations.filter((item) => item.id !== invitationId),
        }),
      ),
    [eventId, deleteThen],
  )

  /** Takes a manager off the event, or, with your own id, leaves it. */
  const removeManager = useCallback(
    (userId: string) =>
      deleteThen(
        () => hostsApi.removeManager(eventId, userId),
        (previous) => ({ ...previous, managers: previous.managers.filter((manager) => manager.user_id !== userId) }),
      ),
    [eventId, deleteThen],
  )

  return {
    hosts: data?.hosts ?? [],
    invitations: data?.invitations ?? [],
    managers: data?.managers ?? [],
    managerInvitations: (data?.managerInvitations ?? []).map((invitation) =>
      invitation.url || !links[invitation.id] ? invitation : { ...invitation, url: links[invitation.id] },
    ),
    owner: data?.owner ?? null,
    loaded: data !== null,
    loading,
    error,
    unavailable,
    load,
    invite,
    revoke,
    removeHost,
    inviteManager,
    createManagerLink,
    revokeManagerInvitation,
    removeManager,
  }
}

export type EventPeople = ReturnType<typeof useEventHosts>
